import { Badge, Box, Divider, Group, Stack, Text } from '@mantine/core';
import { Fragment } from 'react';
import type { WorkItemDto } from '@mexp/shared';
import { FileList } from '@/features/files/FileList';
import { formatDateTime } from '@/lib/format';

/**
 * Every version of a task's upload, newest first. Each version keeps its files, who uploaded them, and why it was
 * replaced: the change RA asked for, with its comment and any marked-up files. A task that answers a client query
 * calls its uploads compliance (Compliance 1, 2, …) instead of versions.
 */
export function TaskVersions({ item, onBehalfLabel }: { item: WorkItemDto; onBehalfLabel?: string }) {
  const noun = item.queryId ? 'Compliance' : 'Version';
  const rounds = [...new Set(item.submissions.map((s) => s.round))].sort((a, b) => b - a);
  if (!rounds.length) {
    return (
      <Text fz="sm" c="dimmed">
        Nothing submitted yet.
      </Text>
    );
  }
  const latest = rounds[0];
  return (
    <Stack gap="md">
      {rounds.map((round, index) => {
        const uploads = item.submissions.filter((s) => s.round === round);
        // Returning a version moves the task on to the next one, so each version is returned at most once.
        const change = item.returns.find((r) => r.round === round) ?? null;
        const current = round === latest && !change;
        const state = change
          ? { label: change.kind === 'reopened' ? 'Reopened after verification' : 'Changes requested', color: 'orange' }
          : current && item.status === 'verified'
            ? { label: 'Verified', color: 'teal' }
            : current && item.status === 'submitted'
              ? { label: 'Waiting for verification', color: 'yellow' }
              : current
                ? { label: 'Current', color: 'gray' }
                : null;
        return (
          <Fragment key={round}>
            {index > 0 && <Divider />}
            <Stack gap={8}>
              <Group gap="xs">
                <Text fz="sm" fw={600}>
                  {noun} {round}
                </Text>
                {state && (
                  <Badge size="sm" variant="light" color={state.color}>
                    {state.label}
                  </Badge>
                )}
              </Group>
              {uploads.map((s, i) => (
                <Stack key={`${s.submittedAt}-${i}`} gap={4}>
                  <Text fz="xs" c="dimmed">
                    {s.submittedBy.name}
                    {s.onBehalf ? (onBehalfLabel ?? ` for ${item.assignee.name}`) : ''} · {formatDateTime(s.submittedAt)}
                  </Text>
                  {s.note && (
                    <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {s.note}
                    </Text>
                  )}
                  <FileList files={s.files} />
                </Stack>
              ))}
              {change && (
                <Box p="sm" bg="var(--mantine-color-orange-light)" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
                  <Stack gap={4}>
                    <Text fz="xs" fw={600} c="var(--mantine-color-orange-light-color)">
                      {change.kind === 'reopened' ? 'Reopened' : 'Changes requested'} by {change.returnedBy.name} · {formatDateTime(change.returnedAt)}
                    </Text>
                    <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {change.reason}
                    </Text>
                    {change.files.length > 0 && <FileList files={change.files} />}
                  </Stack>
                </Box>
              )}
              {current && item.status === 'verified' && item.verifiedBy && (
                <Text fz="xs" c="dimmed">
                  Verified by {item.verifiedBy.name} · {formatDateTime(item.verifiedAt)}
                  {item.verificationNote ? ` · ${item.verificationNote}` : ''}
                </Text>
              )}
            </Stack>
          </Fragment>
        );
      })}
    </Stack>
  );
}
